'use client'
import { gapping, padding } from '@/components/spacing'
import Typography from '@/components/typography/Typography'
import { useT } from '@/phrase/hooks/useT'
import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
// Figma-provided remote assets (valid for ~7 days)
// const IMG_FDW_LOGO_NAV =
//   'https://www.figma.com/api/mcp/asset/d15b59c9-a8fe-465c-8e7e-2f0e7c0321fe'

interface SocialItemType {
  id: number
  title: string
  url: string
  icon: string
}

const SocialData: SocialItemType[] = [
  {
    id: 1,
    title: 'facebook',
    url: 'https://www.facebook.com/official.freedomworld',
    icon: '/svgs/ico_facebook.svg',
  },
  {
    id: 2,
    title: 'tiktok',
    url: 'https://www.tiktok.com/@freedomworldofficial',
    icon: '/svgs/ico_tiktok.svg',
  },
  {
    id: 3,
    title: 'x',
    url: 'https://x.com/fdmetaverse',
    icon: '/svgs/ico_twitter.svg',
  },
  {
    id: 4,
    title: 'instagram',
    url: 'https://www.instagram.com/freedomworld_official/',
    icon: '/svgs/ico_Instagram.svg',
  },
  {
    id: 5,
    title: 'linkedin',
    url: 'https://www.linkedin.com/company/freedomverse',
    icon: '/svgs/ico_linkedin.svg',
  },
  {
    id: 6,
    title: 'discord',
    url: 'https://discord.gg/freedomworld',
    icon: '/svgs/ico_Discord.svg',
  },
  {
    id: 7,
    title: 'telegram',
    url: 'https://t.me/fdmetaverse',
    icon: '/svgs/ico_telegram.svg',
  },
]

interface ItemType {
  id: number
  title: string
  url: string
}

interface GenerateListProps {
  title: string
  data: ItemType[]
}

const getStartedData: ItemType[] = [
  {
    id: 1,
    title: 'footer_createacommunity',
    url: 'https://console.freedom.world',
  },
  // {
  //   id: 2,
  //   title: 'footer_joinacommunity',
  //   url: 'https://info.freedom.world/join-a-community',
  // },
  {
    id: 3,
    title: 'footer_freedomconsole',
    url: 'https://console.freedom.world/',
  },
  {
    id: 4,
    title: 'footer_bookademo',
    url: 'https://freedom.world/community-features/request-demo',
  },
  {
    id: 5,
    title: 'footer_downloadapp',
    url: 'https://freedom.world/community-features/request-demo',
  },
]
const productsData: ItemType[] = [
  {
    id: 1,
    title: 'footer_scape',
    url: 'https://blog.freedom.world/feature-the-scape',
  },
  {
    id: 2,
    title: 'footer_fdm',
    url: 'https://freedom.world/fdm',
  },
  {
    id: 3,
    title: 'footer_fds',
    url: 'https://blog.freedom.world/blog/freedom-shards-the-metaverse-credits-for-the-freedom-ecosystem',
  },
  {
    id: 4,
    title: 'footer_fdc',
    url: 'https://freedom.world/freedomcard',
  },
  {
    id: 5,
    title: 'footer_planets',
    url: 'https://blog.freedom.world/planets',
  },
  // {
  //   id: 6,
  //   title: 'footer_arcade',
  //   url: 'https://blog.freedom.world/impact',
  // },
]

const resourcesData: ItemType[] = [
  {
    id: 1,
    title: 'footer_freedomblog',
    url: 'https://freedom.world/blog',
  },
  {
    id: 2,
    title: 'footer_privacypolicy',
    url: 'https://freedom.world/privacy-policy',
  },
  {
    id: 3,
    title: 'footer_childsafetypolicy',
    url: 'https://blog.freedom.world/child-sexual-abuse-policy',
  },
  {
    id: 4,
    title: 'footer_freedommerchantpolicy',
    url: 'https://blog.freedom.world/terms-conditions-for-merchant-application',
  },
  // {
  //   id: 5,
  //   title: 'footer_banishment',
  //   url: 'https://info.freedom.world/banishment',
  // },
]

const GenerateList = ({ title, data }: GenerateListProps) => {
  const t = useT()

  const [open, setOpen] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState('0px')

  const handleDownloadClick = (e: React.MouseEvent, item: ItemType) => {
    if (item.title === 'footer_downloadapp') {
      e.preventDefault()

      const userAgent = navigator.userAgent || navigator.vendor

      // Detect iOS
      if (/iPad|iPhone|iPod/.test(userAgent)) {
        window.open(
          'https://apps.apple.com/my/app/freedom-world-social-chat/id1606936073',
          '_blank',
        )
      }
      // Detect Android
      else if (/android/i.test(userAgent)) {
        window.open(
          'https://play.google.com/store/apps/details?id=com.bitazza.freedom.wallet&hl=en',
          '_blank',
        )
      }
      // Default to App Store for desktop/unknown
      else {
        window.open(
          'https://apps.apple.com/my/app/freedom-world-social-chat/id1606936073',
          '_blank',
        )
      }
    }
  }

  useEffect(() => {
    if (open && contentRef.current) {
      setHeight(`${contentRef.current.scrollHeight}px`)
    } else {
      setHeight('0px')
    }
  }, [open])

  return (
    <div className="flex-1 px-4 w-full">
      <div className="hidden md:flex flex-col gap-5">
        <Typography
          variant="buttonMedium"
          type="h4"
          weight="900"
          className="uppercase font-black"
        >
          {t(title)}
        </Typography>
        <div
          className={clsx(
            'flex flex-col',
            gapping('Footer/Spacing/space-between-buttons'),
          )}
        >
          {data.map((item) => (
            <Link
              key={item.id}
              href={item.url}
              onClick={(e) => handleDownloadClick(e, item)}
            >
              <Typography variant="bodyMedium" type="p" weight="400">
                {t(item.title)}
              </Typography>
            </Link>
          ))}
        </div>
      </div>

      <div className="md:hidden flex flex-col gap-5  py-4">
        <button
          className="w-full text-left flex justify-between items-center font-semibold"
          onClick={() => setOpen(!open)}
        >
          <Typography
            variant="buttonMedium"
            type="h4"
            weight="900"
            className="uppercase font-black"
          >
            {t(title)}
          </Typography>

          <span
            className={`text-2xl transition-transform duration-300 ${
              open ? 'rotate-180' : ''
            }`}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>
        <div
          ref={contentRef}
          className="transition-all duration-300 ease-in-out overflow-hidden"
          style={{ maxHeight: height }}
        >
          <div
            className={clsx(
              'flex flex-col',
              gapping('Footer/Spacing/space-between-buttons'),
            )}
          >
            {data.map((item) => (
              <Link
                key={item.id}
                href={item.url}
                onClick={(e) => handleDownloadClick(e, item)}
              >
                <Typography variant="bodyMedium" type="p" weight="400">
                  {t(item.title)}
                </Typography>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

const SocialList = ({}: GenerateListProps) => {
  const t = useT()

  return (
    <div className="flex-1 p-4 pt-0 ">
      <div className="flex flex-col gap-5">
        <Typography
          variant="buttonMedium"
          type="h4"
          weight="900"
          className="uppercase font-black"
        >
          {t('footer_community_title')}
        </Typography>
        <div className="flex flex-row md:flex-wrap flex-nowrap gap-3 md:max-w-40 max-w-max">
          {SocialData.map((item) => (
            <Link href={item.url} key={item.title}>
              <Image
                src={item.icon}
                alt={item.title}
                width={32}
                height={32}
                className="w-8 h-8"
              />
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

const Footer = () => {
  const t = useT()

  return (
    <div
      className={clsx(
        'bg-blue-main text-white flex flex-col ',
        padding('Footer/Spacing/padding'),
      )}
    >
      <div
        className={clsx(
          'w-full max-w-360 mx-auto flex flex-col ',
          gapping('Footer/Spacing/main-to-tagline'),
        )}
      >
        <div
          className={clsx(
            'flex flex-col md:flex-row',
            gapping('Footer/Spacing/logo-to-main'),
          )}
        >
          <div className="flex md:justify-start justify-center pr-4">
            <Link href="/">
              <Image
                src="/svgs/freedom-logo.svg"
                alt="Freedom World Logo"
                width={189}
                height={60}
                priority={true}
              />
            </Link>
          </div>
          <div
            className={clsx(
              'flex md:flex-row flex-col flex-1',
              // gapping('Footer/Spacing/space-between-columns'),
            )}
          >
            <GenerateList
              title="footer_getstarted_title"
              data={getStartedData}
            />
            <GenerateList title="footer_ecosystem_title" data={productsData} />
            <GenerateList title="footer_support_title" data={resourcesData} />
            {/* <GenerateList title="Resources" data={resourcesData} /> */}
            <SocialList title="footer_community_title" data={resourcesData} />
          </div>
        </div>
        <Typography variant="bodyXSmall" type="p" weight="400" className="px-4">
          {t('footer_freedomverse_copyright')}
        </Typography>
      </div>
    </div>
  )
}

export default Footer
